export type ManifestStatus = 'ok' | 'stale' | 'ambiguous' | 'unknown';

export interface ManifestComparison {
  status: ManifestStatus;
  serverMain?: string;
  loadedMain?: string;
  mainCount?: number;
}

const MAIN_BUNDLE_PATTERN = /^main\.[a-z0-9]+\.js$/i;

const fileNameOf = (url: string): string => url.split('?')[0].split('/').pop() ?? '';

/**
 * Compares the version described in ngsw.json with the version actually loaded in the document.
 *
 * A correct build's manifest lists exactly one main.*.js. More than one means the
 * dist directory was not cleaned — the comparison is then ambiguous and no action
 * must be taken.
 */
export function compareManifest(hashTable: Record<string, string>, loadedScriptUrls: string[]): ManifestComparison {
  const serverMains = Object.keys(hashTable ?? {})
    .map(path => fileNameOf(path))
    .filter(name => MAIN_BUNDLE_PATTERN.test(name));

  if (serverMains.length === 0) {
    return { status: 'unknown', mainCount: 0 };
  }

  if (serverMains.length > 1) {
    return { status: 'ambiguous', mainCount: serverMains.length };
  }

  const loadedMain = (loadedScriptUrls ?? []).map(fileNameOf).find(name => MAIN_BUNDLE_PATTERN.test(name));

  if (!loadedMain) {
    return { status: 'unknown', mainCount: 1 };
  }

  const serverMain = serverMains[0];

  return serverMain === loadedMain
    ? { status: 'ok', serverMain, loadedMain }
    : { status: 'stale', serverMain, loadedMain };
}
